import type { Dict } from "../types";

export const ai: Dict = {
  en: {

    // AI Configuration module
    "AIConfig.title": "AI Voice Agent",
    "AIConfig.intro": "Activate your AI voice receptionist. Once active, it will answer calls, take appointments, and manage reschedules automatically.",
    "AIConfig.activateButton": "Activate AI",
    "AIConfig.ownerOnlyNote": "Only the salon owner can activate the AI agent.",
    "AIConfig.provisioningTitle": "Activating your AI agent…",
    "AIConfig.step.buyNumber": "Purchasing a phone number",
    "AIConfig.step.importData": "Importing your salon data",
    "AIConfig.step.createAgent": "Creating the voice agent",
    "AIConfig.step.link": "Linking everything together",
    "AIConfig.failedActionable": "Activation failed: action required",
    "AIConfig.failedTransient": "Activation failed: please retry",
    "AIConfig.retry": "Retry",
    "AIConfig.addressEditLabel": "Salon address (required for provisioning)",
    "AIConfig.addressEditPlaceholder": "123 Main Street, City, Province",
    "AIConfig.addressSaved": "Address saved",
    "AIConfig.addressSaveError": "Could not save address",
    "AIConfig.activeBanner": "AI active · {phone}",
    "AIConfig.activateSuccess": "AI agent activated successfully",
    "AIConfig.activateError": "Could not activate AI agent",
    "AIConfig.firstMessage": "Welcome message",
    "AIConfig.firstMessagePlaceholder": "Hello, thank you for calling Salon Élégance. How can I help you?",
    "AIConfig.voice": "AI Voice",
    "AIConfig.primaryLanguage": "Primary language",
    "AIConfig.languageFr": "French",
    "AIConfig.languageEn": "English",
    "AIConfig.secondaryLanguage": "Secondary language",
    "AIConfig.secondaryLanguageNone": "None",
    "AIConfig.languageEs": "Spanish",
    "AIConfig.settings": "Settings",
    "AIConfig.settingsDesc": "Adjust the voice, languages, greeting and transfer.",
    "AIConfig.persona.subtitle": "your receptionist",
    "AIConfig.persona.fallbackName": "Your AI receptionist",
    "AIConfig.persona.inService": "Active",
    "AIConfig.persona.transfersTo": "transfers to",
    "AIConfig.persona.greetingPreview": "Greeting preview",
    "AIConfig.stats.calls": "Calls this month",
    "AIConfig.stats.minutes": "Minutes",
    "AIConfig.stats.minUnit": "min",
    "AIConfig.stats.successRate": "Success rate",
    "AIConfig.tabConfiguration": "Configuration",
    "AIConfig.tabConversations": "Conversations",
    "AIConv.searchPlaceholder": "Search conversations...",
    "AIConv.colTitle": "Title",
    "AIConv.colDate": "Date",
    "AIConv.colDuration": "Duration",
    "AIConv.colMessages": "Messages",
    "AIConv.colStatus": "Status",
    "AIConv.statusSuccess": "Successful",
    "AIConv.statusFailed": "Failed",
    "AIConv.statusUnknown": "Unknown",
    "AIConv.untitled": "Call on {date}",
    "AIConv.summary": "Summary",
    "AIConv.action.query": "Information request",
    "AIConv.action.booking": "Booking",
    "AIConv.action.reschedule": "Reschedule",
    "AIConv.action.cancellation": "Cancellation",
    "AIConv.transcript": "Transcript",
    "AIConv.noTranscript": "No transcript available.",
    "AIConv.empty": "No conversations yet.",
    "AIConv.messagesShort": "msg",
    "AIConv.listen": "Play recording",
    "AIConv.loadingAudio": "Loading…",
    "AIConv.audioUnavailable": "Recording unavailable.",
    "AIConv.filterDate": "Date",
    "AIConv.filterAllDates": "All dates",
    "AIConv.filterDuration": "Duration",
    "AIConv.filterLanguage": "Language",
    "AIConv.filterPhone": "Phone number",
    "AIConv.filterNone": "No value available.",
    "AIConv.clearFilters": "Clear filters",
    "AIConv.clear": "Clear",
    "AIConv.colPhone": "Phone",
    "AIConv.colLanguage": "Language",
    "AIConv.dur.lt1": "< 1 min",
    "AIConv.dur.1to3": "1 to 3 min",
    "AIConv.dur.gt3": "> 3 min",
    "AIConfig.transferNumber": "Transfer number",
    "AIConfig.transferNumberPlaceholder": "+15141234567",
    "AIConfig.transferNumberInvalid": "Invalid format. Example: +15141234567",
    "AIConfig.saveConfig": "Save configuration",
    "AIConfig.configSaved": "Configuration saved",
    "AIConfig.configSaveError": "Could not save configuration",
  },
  fr: {

    // Module IA vocale
    "AIConfig.title": "Agent vocal IA",
    "AIConfig.intro": "Activez votre réceptionniste vocal IA. Une fois actif, il répondra aux appels, prendra des rendez-vous et gérera les reports automatiquement.",
    "AIConfig.activateButton": "Activer l'IA",
    "AIConfig.ownerOnlyNote": "Seul le propriétaire du salon peut activer l'agent IA.",
    "AIConfig.provisioningTitle": "Activation de votre agent IA en cours…",
    "AIConfig.step.buyNumber": "Achat du numéro de téléphone",
    "AIConfig.step.importData": "Import des données du salon",
    "AIConfig.step.createAgent": "Création de l'agent vocal",
    "AIConfig.step.link": "Liaison et finalisation",
    "AIConfig.failedActionable": "Activation échouée : action requise",
    "AIConfig.failedTransient": "Activation échouée : veuillez réessayer",
    "AIConfig.retry": "Réessayer",
    "AIConfig.addressEditLabel": "Adresse du salon (requise pour l'activation)",
    "AIConfig.addressEditPlaceholder": "123 rue Principale, Ville, Province",
    "AIConfig.addressSaved": "Adresse enregistrée",
    "AIConfig.addressSaveError": "Impossible d'enregistrer l'adresse",
    "AIConfig.activeBanner": "IA active · {phone}",
    "AIConfig.activateSuccess": "Agent IA activé avec succès",
    "AIConfig.activateError": "Impossible d'activer l'agent IA",
    "AIConfig.firstMessage": "Message d'accueil",
    "AIConfig.firstMessagePlaceholder": "Bonjour, merci d'appeler le Salon Élégance. Comment puis-je vous aider ?",
    "AIConfig.voice": "Voix de l'IA",
    "AIConfig.primaryLanguage": "Langue principale",
    "AIConfig.languageFr": "Français",
    "AIConfig.languageEn": "Anglais",
    "AIConfig.secondaryLanguage": "Langue secondaire",
    "AIConfig.secondaryLanguageNone": "Aucune",
    "AIConfig.languageEs": "Espagnol",
    "AIConfig.settings": "Réglages",
    "AIConfig.settingsDesc": "Ajustez la voix, les langues, l'accueil et le transfert.",
    "AIConfig.persona.subtitle": "votre réceptionniste",
    "AIConfig.persona.fallbackName": "Votre réceptionniste IA",
    "AIConfig.persona.inService": "En service",
    "AIConfig.persona.transfersTo": "transfère →",
    "AIConfig.persona.greetingPreview": "Aperçu du message d'accueil",
    "AIConfig.stats.calls": "Appels ce mois",
    "AIConfig.stats.minutes": "Minutes",
    "AIConfig.stats.minUnit": "min",
    "AIConfig.stats.successRate": "Taux de réussite",
    "AIConfig.tabConfiguration": "Configuration",
    "AIConfig.tabConversations": "Conversations",
    "AIConv.searchPlaceholder": "Rechercher une conversation...",
    "AIConv.colTitle": "Titre",
    "AIConv.colDate": "Date",
    "AIConv.colDuration": "Durée",
    "AIConv.colMessages": "Messages",
    "AIConv.colStatus": "Statut",
    "AIConv.statusSuccess": "Réussi",
    "AIConv.statusFailed": "Échec",
    "AIConv.statusUnknown": "Inconnu",
    "AIConv.untitled": "Appel du {date}",
    "AIConv.summary": "Résumé",
    "AIConv.action.query": "Demande d'information",
    "AIConv.action.booking": "Réservation",
    "AIConv.action.reschedule": "Report",
    "AIConv.action.cancellation": "Annulation",
    "AIConv.transcript": "Transcription",
    "AIConv.noTranscript": "Aucune transcription disponible.",
    "AIConv.empty": "Aucune conversation pour l'instant.",
    "AIConv.messagesShort": "msg",
    "AIConv.listen": "Écouter l'enregistrement",
    "AIConv.loadingAudio": "Chargement…",
    "AIConv.audioUnavailable": "Enregistrement indisponible.",
    "AIConv.filterDate": "Date",
    "AIConv.filterAllDates": "Toutes les dates",
    "AIConv.filterDuration": "Durée",
    "AIConv.filterLanguage": "Langue",
    "AIConv.filterPhone": "Numéro de téléphone",
    "AIConv.filterNone": "Aucune valeur disponible.",
    "AIConv.clearFilters": "Effacer les filtres",
    "AIConv.clear": "Effacer",
    "AIConv.colPhone": "Numéro",
    "AIConv.colLanguage": "Langue",
    "AIConv.dur.lt1": "< 1 min",
    "AIConv.dur.1to3": "1 à 3 min",
    "AIConv.dur.gt3": "> 3 min",
    "AIConfig.transferNumber": "Numéro de transfert",
    "AIConfig.transferNumberPlaceholder": "+15141234567",
    "AIConfig.transferNumberInvalid": "Format invalide. Exemple : +15141234567",
    "AIConfig.saveConfig": "Enregistrer la configuration",
    "AIConfig.configSaved": "Configuration enregistrée",
    "AIConfig.configSaveError": "Impossible d'enregistrer la configuration",
  },
};
